import {
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
  type JSX,
  useEffect,
} from 'react';
import { useIntl } from 'react-intl';

import { Checkbox } from '../../../../forms/checkbox/Checkbox.js';
import { Tooltip } from '../../../../overlays/tooltip/Tooltip.js';
import { DataTableDefaultCell } from '../../components/DataTableDefaultCell.js';
import { type Row, Table } from '../../hooks/useTable/types.js';
import { dataTableMessages } from '../../messages.js';

/**
 * DataTable row selection header.
 * @internal
 */
export function RowSelectionHeader(
  props: Readonly<{
    table: Table<unknown>;
  }>,
) {
  const { table } = props;
  const resetBatchSelection = table.resetBatchSelection;
  const value = getCheckboxValue(table);
  const isDisabled = isSelectAllDisabled(table);

  const intl = useIntl();
  const checkboxMessage = intl.formatMessage(
    dataTableMessages.selectAllTooltip,
    {
      nextSelection:
        value === false || value === 'indeterminate' ? 'select' : 'deselect',
      scope: table.options.rowSelectionSelectAllMode,
    },
  );

  useEffect(() => {
    document.addEventListener('click', resetBatchSelection);
    return () => {
      document.removeEventListener('click', resetBatchSelection);
    };
  }, [resetBatchSelection]);

  function toggleSelectAll(
    event: ChangeEvent<HTMLInputElement> | undefined,
  ): void {
    if (isDisabled) {
      event?.preventDefault();
      return;
    }
    if (table.options.rowSelectionSelectAllMode === 'page') {
      if (table.options.rowSelectionLimit !== undefined) {
        table.getToggleAllLimitedPageRowsSelectedHandler()(event);
      } else {
        table.getToggleAllPageRowsSelectedHandler()(event);
      }
    } else if (table.options.rowSelectionSelectAllMode === 'all') {
      if (table.options.rowSelectionLimit !== undefined) {
        table.getToggleAllLimitedRowsSelectedHandler()(event);
      } else {
        table.getToggleAllRowsSelectedHandler()(event);
      }
    }
  }

  const selectAllDtProperties = `state:${
    value === true ? 'deselect' : 'select'
  }`;

  const checkbox = (
    <Checkbox
      value={isDisabled ? false : value}
      aria-disabled={isDisabled}
      aria-label={checkboxMessage}
      onChange={(_, event) => toggleSelectAll(event)}
      data-dt-name="RowSelectDeselectAll"
      data-dt-properties={selectAllDtProperties}
    />
  );

  return (
    <DataTableDefaultCell>
      {isDisabled ? (
        checkbox
      ) : (
        <Tooltip text={checkboxMessage}>{checkbox}</Tooltip>
      )}
    </DataTableDefaultCell>
  );
}

/**
 * @returns true, if the 'select all' checkbox shall be disabled, false otherwise.
 */
function isSelectAllDisabled(table: Table<unknown>): boolean {
  const isTableEmpty = table.getRowCount() === 0;
  if (isTableEmpty) {
    return true;
  }
  let isAllRowsSelectionDisabled = false;
  if (table.options.rowSelectionSelectAllMode === 'page') {
    isAllRowsSelectionDisabled = table.getIsAllPageRowsSelectionDisabled();
  } else if (table.options.rowSelectionSelectAllMode === 'all') {
    isAllRowsSelectionDisabled = table.getIsAllRowsSelectionDisabled();
  }
  return isAllRowsSelectionDisabled;
}

/**
 * @returns the current state of the 'select all' checkbox
 */
function getCheckboxValue(table: Table<unknown>): boolean | 'indeterminate' {
  if (table.options.rowSelectionSelectAllMode === 'page') {
    if (table.getIsAllPageRowsSelected()) {
      return true;
    }
    if (table.getIsSomePageRowsSelected()) {
      return 'indeterminate';
    }
    return false;
  }
  if (table.options.rowSelectionSelectAllMode === 'all') {
    if (table.getIsAllRowsSelected()) {
      return true;
    }
    if (table.getIsSomeRowsSelected()) {
      return 'indeterminate';
    }
    return false;
  }
  return false;
}

const resolveCheckboxValue = (row: Row<unknown>): boolean | 'indeterminate' => {
  if (row.subRows.length === 0) {
    return row.getIsSelected();
  }

  if (row.getIsAllSubRowsSelected()) {
    return true;
  }

  if (row.getIsSomeSelected()) {
    return 'indeterminate';
  }

  return false;
};

/**
 * DataTable row selection cell.
 * @internal
 */
export function RowSelectionCell(
  props: Readonly<{
    row: Row<unknown>;
  }>,
): JSX.Element {
  const { row } = props;
  const intl = useIntl();

  const isDisabled = row.getShouldDisableRowSelection();
  const rowDtProperties = `state:${
    resolveCheckboxValue(row) === true ? 'deselect' : 'select'
  }`;

  return (
    <DataTableDefaultCell
      onMouseDown={(event: ReactMouseEvent) => {
        document.getSelection()?.removeAllRanges();
        event.preventDefault();
      }}
    >
      <Checkbox
        aria-label={intl.formatMessage(dataTableMessages.selectRowCheckbox, {
          id: row.id,
        })}
        value={resolveCheckboxValue(row)}
        aria-disabled={isDisabled}
        onChange={(value, event) => {
          if (isDisabled) {
            event?.preventDefault();
            return;
          }
          row.batchSelect(value, (event?.nativeEvent as MouseEvent).shiftKey);
        }}
        data-row-selection-checkbox
        data-dt-name="RowSelectDeselect"
        data-dt-properties={rowDtProperties}
      />
    </DataTableDefaultCell>
  );
}
